import React, {
  FunctionComponent,
  ChangeEvent,
  useState,
  useContext,
} from "react";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import { FocusableHeader, LoadingMask } from "../../../components";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  serializePayload,
  formatMCTDate,
  formatPhone,
  EnrollmentPages,
  getValidationObject,
  logError,
  isPdPlanType,
  isMaPlanType,
} from "../../../helpers";
import PlanInfo from "./../PlanInfo";
import ReviewBox from "./../ReviewBox";
import {
  Title,
  Sex,
  EnrollResponseCode,
  GetBenefitsFrom,
} from "../../../@types";
import states from "./../states";
import { Choice } from "@cmsgov/ds-medicare-gov";
import { AppContext } from "../../../app/store";
import { submitEnrollment } from "../../../api";
import FormErrorPage from "./../FormErrorPage";
import {
  isPffsMa,
  isCostPlanWithDrugs,
  isCostPlanWithoutDrugs,
} from "../../../helpers/enrollmentFormHelpers";

const Review: FunctionComponent<EnrollmentFormProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  goToPage,
}) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  const isPdp = isPdPlanType(plan.plan_type);
  const maOnly = isMaPlanType(plan.plan_type);
  const pffsMa = isPffsMa(plan);
  const costPlanWithDrugs = isCostPlanWithDrugs(plan);
  const costPlanWithoutDrugs = isCostPlanWithoutDrugs(plan);

  const next = (): void => {
    setSubmitted(true);

    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.Review
    );

    if (formStatus.readAndUnderstoodContentsValidity) {
      setLoading(true);

      submitEnrollment(serializePayload(enrollData, state))
        .then(res => {
          setLoading(false);

          if (res?.status === EnrollResponseCode.OEC_VALID) {
            setEnrollData({
              ...enrollData,
              confirmationNumber: res.confirmation_number,
            });
            nextPage();
          } else {
            throw res?.status ?? EnrollResponseCode.OEC_UNKNOWN_ERROR;
          }
        })
        .catch(e => {
          logError(
            "Failed to submit enrollment (EnrollmentFormPage/Review)",
            e
          );
          setError(EnrollResponseCode.OEC_UNKNOWN_ERROR);
        });
    }
  };

  const getState = (fips: string): string => {
    const state = states.find(s => s.fipsCode === fips);
    return state ? state.name : "N/A";
  };

  const titleTranslations = {
    [Title.MR]: t("enroll_form.mr"),
    [Title.MRS]: t("enroll_form.mrs"),
    [Title.MS]: t("enroll_form.ms"),
    [Title.MISS]: t("enroll_form.miss"),
  };

  let personalInfoItems = [
    {
      label: t("enroll_form.title"),
      value: titleTranslations[enrollData.title],
    },
    {
      label: t("enroll_form.first_name"),
      value: enrollData.firstName,
    },
    {
      label: t("enroll_form.middle_initial"),
      value: enrollData.middleInitial,
    },
    {
      label: t("enroll_form.last_name"),
      value: enrollData.lastName,
    },
    {
      label: t("csr_landing.dob"),
      value: formatMCTDate(enrollData.dob),
    },
    {
      label: t("enroll_form.sex"),
      value:
        enrollData.sex === Sex.MALE
          ? t("enroll_form.male")
          : t("enroll_form.female"),
    },
    {
      label: t("enroll_form.primary_phone"),
      value: formatPhone(enrollData.primaryPhone),
    },

    {
      label: t("enroll_form.email"),
      value: enrollData.email,
    },
    {
      label: t("enroll_form.emergency_contact"),
      value: enrollData.provideEmergencyContact ? t("yes") : t("no"),
    },
  ];

  if (enrollData.provideEmergencyContact) {
    personalInfoItems = [
      ...personalInfoItems,
      ...[
        {
          label: t("enroll_form.name"),
          value: enrollData.emergencyContactName,
        },
        {
          label: t("enroll_form.phone_number"),
          value: formatPhone(enrollData.emergencyContactPhone),
        },
        {
          label: t("enroll_form.relationship"),
          value: enrollData.emergencyContactRelationship,
        },
      ],
    ];
  }

  let generalQuestions: { label: string; value: string }[] = [];

  if (!isPdp) {
    generalQuestions.push({
      label: t("enroll_form.renal_disease_question"),
      value: enrollData.renalDisease ? t("yes") : t("no"),
    });
  }

  if (!maOnly && !pffsMa && !costPlanWithoutDrugs) {
    generalQuestions.push({
      label: t("enroll_form.additional_drug_coverage_question"),
      value: enrollData.hasAdditionalDrugCoverage ? t("yes") : t("no"),
    });

    if (enrollData.hasAdditionalDrugCoverage) {
      generalQuestions = [
        ...generalQuestions,
        ...[
          {
            label: t("enroll_form.additional_drug_coverage.name"),
            value: enrollData.additionalDrugCoverage.name,
          },
          {
            label: t("enroll_form.additional_drug_coverage.id"),
            value: enrollData.additionalDrugCoverage.id,
          },
          {
            label: t("enroll_form.additional_drug_coverage.group"),
            value: enrollData.additionalDrugCoverage.group,
          },
        ],
      ];
    }
  }

  generalQuestions.push({
    label: t("enroll_form.long_term_care_question"),
    value: enrollData.inLongTermCareFacility ? t("yes") : t("no"),
  });

  if (enrollData.inLongTermCareFacility) {
    generalQuestions = [
      ...generalQuestions,
      ...[
        {
          label: t("enroll_form.long_term_care_facility.name"),
          value: enrollData.longTermCareFacility.name,
        },
        {
          label: t("enroll_form.address.line_1"),
          value: enrollData.longTermCareFacility.addressLine1,
        },
        {
          label: t("enroll_form.address.city"),
          value: enrollData.longTermCareFacility.city,
        },
        {
          label: t("enroll_form.address.state"),
          value: getState(enrollData.longTermCareFacility.state),
        },
        {
          label: t("enroll_form.address.zip"),
          value: enrollData.longTermCareFacility.zip,
        },
        {
          label: t("enroll_form.phone_number"),
          value: formatPhone(enrollData.longTermCareFacility.phone),
        },
      ],
    ];
  }

  if (!isPdp) {
    generalQuestions.push({
      label: t("enroll_form.are_you_enrolled_in_medicaid"),
      value: enrollData.enrolledInMedicaid ? t("yes") : t("no"),
    });
  }

  if (!isPdp && !costPlanWithDrugs) {
    generalQuestions.push({
      label: t("enroll_form.health_coverage_through_employer_question"),
      value: enrollData.employerHealthCoverage ? t("yes") : t("no"),
    });
  }

  if (!isPdp) {
    generalQuestions.push({
      label: t("enroll_form.primary_care_name"),
      value: enrollData.primaryCareName,
    });
  }

  const paying = [
    {
      label: t("enroll_form.how_do_you_want_to_pay"),
      value: enrollData.premiumDirectPay
        ? t("enroll_form.have_the_plan_bill_me")
        : t("enroll_form.deduct_from_check"),
    },
  ];

  if (enrollData.premiumDeducted) {
    paying.push({
      label: t("enroll_form.i_get_benefits_from"),
      value:
        enrollData.premiumWithhold === GetBenefitsFrom.RRB
          ? t("rrb")
          : t("social_security"),
    });
  }

  const planAgreement = {
    section: {
      title: enrollData.isEnrollee
        ? t("enroll_form.agreement.i_am_person_on_enrollment_form")
        : t("enroll_form.agreement.i_am_authorized_to_act_on_behalf"),
      items: !enrollData.isEnrollee
        ? [
            {
              label: t("enroll_form.authorized_representative.name"),
              value: enrollData.authorizedRepresentative.name,
            },
            {
              label: t("enroll_form.authorized_representative.relationship"),
              value: enrollData.authorizedRepresentative.relationship,
            },
            {
              label: t("enroll_form.address.line_1"),
              value: enrollData.authorizedRepresentative.addressLine1,
            },
            {
              label: t("enroll_form.address.city"),
              value: enrollData.authorizedRepresentative.city,
            },
            {
              label: t("enroll_form.address.state"),
              value: getState(enrollData.authorizedRepresentative.state),
            },
            {
              label: t("enroll_form.address.zip"),
              value: enrollData.authorizedRepresentative.zip,
            },
            {
              label: t("enroll_form.phone_number"),
              value: formatPhone(enrollData.authorizedRepresentative.phone),
            },
          ]
        : [],
    },
  };

  return !error ? (
    <div>
      <LoadingMask
        loading={loading}
        message={t("enroll_form.sending_enrollment")}
      />

      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.review_your_application")}
      />

      <PlanInfo plan={plan} />

      <ReviewBox
        key="your_medicare_info"
        title={t("enrollment_form.your_medicare_info")}
        goToPage={(): void => goToPage(EnrollmentPages.MedicareInfo)}
        items={[
          {
            label: t("enroll_form.medicare_number"),
            value: enrollData.medicareNumber,
          },
          {
            label: t("enroll_form.part_a_coverage_starts"),
            value: formatMCTDate(enrollData.partAStart),
          },
          {
            label: t("enroll_form.part_b_coverage_starts"),
            value: formatMCTDate(enrollData.partBStart),
          },
        ]}
      />

      <ReviewBox
        key="your_personal_info"
        title={t("enrollment_form.your_personal_info")}
        goToPage={(): void => goToPage(EnrollmentPages.PersonalInfo)}
        items={personalInfoItems}
      />

      <ReviewBox
        key="your_address"
        title={t("enrollment_form.your_address")}
        goToPage={(): void => goToPage(EnrollmentPages.Address)}
        items={{
          permResidence: {
            title: t("enroll_form.perm_residence"),
            items: [
              {
                label: t("enroll_form.address_line_1"),
                value: enrollData.address1,
              },
              {
                label: t("enroll_form.address_line_2"),
                value: enrollData.address2,
              },
              {
                label: t("enroll_form.city"),
                value: enrollData.city,
              },
              {
                label: t("enroll_form.state"),
                value: getState(enrollData.state),
              },
              {
                label: t("enroll_form.zip"),
                value: enrollData.zip,
              },
              {
                label: t("enroll_form.county"),
                value: enrollData.county ? enrollData.county.name : "",
              },
            ],
          },
          mailing: {
            title: t("enroll_form.mailing_address"),
            items: [
              {
                label: t("enroll_form.address_line_1"),
                value: enrollData.mailingAddress1,
              },
              {
                label: t("enroll_form.address_line_2"),
                value: enrollData.mailingAddress2,
              },
              {
                label: t("enroll_form.city"),
                value: enrollData.mailingCity,
              },
              {
                label: t("enroll_form.state"),
                value: getState(enrollData.mailingState),
              },
              {
                label: t("enroll_form.zip"),
                value: enrollData.mailingZip,
              },
            ],
          },
        }}
      />

      <ReviewBox
        key="general_questions"
        title={t("enrollment_form.general_questions")}
        goToPage={(): void => goToPage(EnrollmentPages.GeneralQuestions)}
        items={generalQuestions}
      />

      <ReviewBox
        key="paying_for_your_plan"
        title={t("enrollment_form.paying_for_your_plan")}
        goToPage={(): void => goToPage(EnrollmentPages.Paying)}
        items={paying}
      />

      <ReviewBox
        key="plan_agreement"
        title={t("enrollment_form.plan_agreement")}
        goToPage={(): void => goToPage(EnrollmentPages.Agreement)}
        items={planAgreement}
      />

      <div className="ds-u-margin-top--3">
        <p>{t("enroll_form.people_with_medicare")}</p>
        {submitted && !enrollData.readAndUnderstoodContents && (
          <span className="ds-c-field__hint ds-u-color--error" role="alert">
            {t("enroll_form.check_box")}
          </span>
        )}
        <Choice
          type="checkbox"
          name="read-and-understand-contents"
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              readAndUnderstoodContents: e.target.checked,
            });
          }}
          checked={enrollData.readAndUnderstoodContents}
          value="read-and-understood"
        >
          {t("enroll_form.read_and_understood_contents")}
        </Choice>
      </div>

      <BackNextButtons
        inReview={false}
        nextPage={next}
        nextText={t("enroll_form.submit")}
        previousPage={previousPage}
        hideBackButton={true}
        disableNext={loading}
      />
    </div>
  ) : (
    <FormErrorPage plan={plan} />
  );
};

export default Review;
